import { useState, useEffect, useMemo } from 'react';

import CircleIcon from '@mui/icons-material/Circle';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import PropTypes from 'prop-types';

import AnimationProgress from '../AnimationProgress';
import * as Styled from './CheckIn.style';

/**
 * A component for displaying check-in progress that animates from progress to completion
 * @param {object} props - Component props
 * @param {string} props.title - The title to display during progress
 * @param {string} props.summary - The summary text to display during progress
 * @param {Array} props.checkList - Array of check items to display
 * @param {string} props.finalTitle - The title to display when completed
 * @param {string} props.finalMessage - The message to display when completed
 * @param {boolean} [props.shouldAnimate=true] - Whether to animate the progress sequence or show completed state immediately
 * @param {Function} [props.onAnimationComplete] - Callback fired when the entire animation sequence is complete
 * @returns {import('react').ReactElement} The CheckIn component
 */
function CheckIn({
  title,
  summary,
  checkList = [],
  finalTitle,
  finalMessage,
  shouldAnimate = true,
  onAnimationComplete
}) {
  /** milliseconds between each checklist reveal */
  const itemDelay = 1500;

  const totalSteps = checkList.length;

  const [visibleCount, setVisibleCount] = useState(0);
  const [isCompleted, setIsCompleted] = useState(!shouldAnimate); // If animate=false, start completed

  const prefersReducedMotion = useReducedMotion();

  // derive progress
  const progressValue = useMemo(() => {
    if (totalSteps === 0) return 100; // Show complete progress for empty list
    return Math.round((visibleCount / totalSteps) * 100);
  }, [visibleCount, totalSteps]);

  /* ----
   * Step-wise Checklist reveal
   */

  useEffect(() => {
    // If animate is false, skip animation and show completed state
    if (!shouldAnimate) {
      setVisibleCount(totalSteps);
      setIsCompleted(true);
      if (onAnimationComplete) {
        onAnimationComplete();
      }
      return undefined;
    }

    // Handle empty checkList case - should still show progress briefly then complete

    // reveal next checklist item
    if (visibleCount < totalSteps) {
      const id = setTimeout(() => {
        setVisibleCount((prev) => prev + 1);
      }, itemDelay);
      return () => clearTimeout(id);
    }

    // All items visible, transition to completed after delay
    if (visibleCount === totalSteps) {
      setIsCompleted(true);
      if (onAnimationComplete) {
        onAnimationComplete();
      }
    }

    return undefined;
  }, [visibleCount, totalSteps, itemDelay, shouldAnimate, onAnimationComplete]);

  // Animation Variants
  const listItemsVariants = prefersReducedMotion
    ? {
        hidden: { opacity: 1, x: 0 },
        animate: { opacity: 1, x: 0, transition: { duration: 0 } }
      }
    : {
        hidden: { opacity: 0, x: -8 },
        animate: { opacity: 1, x: 0, transition: { duration: 0.25, ease: 'easeInOut' } }
      };

  const progressCardVariants = {
    // initial: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -20, transition: { duration: 0.3, ease: 'easeInOut' } }
  };

  return (
    <Styled.CheckInRoot>
      <AnimatePresence mode="wait">
        <motion.div key="progress" exit="exit" initial="initial" variants={progressCardVariants}>
          <Styled.ProgressContainer>
            {isCompleted ? (
              <Styled.Title>{finalTitle}</Styled.Title>
            ) : (
              <>
                <Styled.Title>{title}</Styled.Title>
                <Styled.Summary>{summary}</Styled.Summary>
              </>
            )}
            <AnimationProgress height={8} value={progressValue} />
            {totalSteps > 0 && (
              <Styled.CheckList>
                <AnimatePresence mode="wait">
                  {/* eslint-disable react/no-array-index-key -- Index combined with item text for animation keys */}
                  {checkList.slice(0, visibleCount).map((item, index) => (
                    <motion.li
                      key={`item-${item}-${index}`}
                      animate="animate"
                      exit="hidden"
                      initial={prefersReducedMotion ? 'animate' : 'hidden'}
                      layout
                      variants={listItemsVariants}
                    >
                      {/* eslint-enable react/no-array-index-key */}
                      <Styled.CheckItem>
                        <Styled.CheckIcon>
                          <CircleIcon fontSize="small" />
                        </Styled.CheckIcon>
                        <Styled.CheckText>{item}</Styled.CheckText>
                      </Styled.CheckItem>
                    </motion.li>
                  ))}
                </AnimatePresence>
              </Styled.CheckList>
            )}
            {isCompleted && <Styled.FinalMessage>{finalMessage}</Styled.FinalMessage>}
          </Styled.ProgressContainer>
        </motion.div>
      </AnimatePresence>
    </Styled.CheckInRoot>
  );
}

CheckIn.propTypes = {
  title: PropTypes.string.isRequired,
  summary: PropTypes.string.isRequired,
  checkList: PropTypes.arrayOf(PropTypes.string),
  finalTitle: PropTypes.string.isRequired,
  finalMessage: PropTypes.string.isRequired,
  shouldAnimate: PropTypes.bool,
  onAnimationComplete: PropTypes.func
};

export default CheckIn;
